/**
 * DATA BEST PRACTICES GUIDE
 * 
 * Comprehensive guide to help users enter clean, consistent data
 * for the best collection management experience.
 * 
 * Route: /help/data-guide
 */

import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Sparkles, Tag, CheckCircle2, XCircle,
  Lightbulb, ChevronRight
} from 'lucide-react';
import { PageHeader } from '../components/common';

export default function DataGuidePage() {
  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        
        {/* Back Link */}
        <Link
          to="/help"
          className="inline-flex items-center gap-2 text-on-bg hover:text-glass mb-4 font-body"
        >
          <ArrowLeft className="icon-sm" />
          Back to Help Center
        </Link>

        {/* Header */}
        <PageHeader
          icon={BookOpen}
          title="Data Best Practices"
          subtitle="Get the most out of your collection with clean, consistent data"
          className="mb-6"
        />

        {/* Quick Navigation */}
        <div className="glass-card p-6 mb-8">
          <h2 className="text-lg font-bold text-glass mb-4 font-body flex items-center gap-2">
            <ChevronRight className="icon-md" />
            Jump to Section
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {[
              { id: 'why-clean-data', label: 'Why It Matters' },
              { id: 'fields', label: 'Understanding Fields' },
              { id: 'tags', label: 'Tags Guide' },
              { id: 'quick-reference', label: 'Quick Reference' },
              { id: 'mistakes', label: 'Common Mistakes' },
            ].map(section => (
              <a
                key={section.id}
                href={`#${section.id}`}
                className="px-4 py-2 bg-slate-700/50 hover:bg-slate-600/50 rounded-lg text-sm text-glass-secondary hover:text-glass transition-default font-body text-center"
              >
                {section.label}
              </a>
            ))}
          </div>
        </div>

        {/* Why Clean Data Matters */}
        <section id="why-clean-data" className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 font-body flex items-center gap-2">
            <Sparkles className="icon-md text-yellow-400" />
            Why Does Clean Data Matter?
          </h2>
          
          <p className="text-glass-secondary mb-4 font-body">
            Think of your collection like a library. If every book was just tossed on a shelf with no organization, finding anything would be a nightmare! Clean, consistent data helps you:
          </p>
          
          <ul className="space-y-2 mb-4">
            {[
              { title: 'Find things fast', desc: 'Search for "all my holos from 2023" or "plates with florals"' },
              { title: 'Discover patterns', desc: 'See which brands you collect most, or which color families dominate your stash' },
              { title: 'Share accurately', desc: 'Your public wishlist and destash listings look professional' },
              { title: 'Import smoothly', desc: 'If you ever export and re-import, everything stays organized' },
            ].map(item => (
              <li key={item.title} className="flex items-start gap-3">
                <CheckCircle2 className="icon-sm text-green-400 mt-1 flex-shrink-0" />
                <span className="text-glass-secondary font-body">
                  <strong className="text-glass">{item.title}</strong> — {item.desc}
                </span>
              </li>
            ))}
          </ul>
          
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
            <p className="text-yellow-200 font-body text-sm">
              <strong className="text-yellow-300">💡 Pro tip:</strong> The few extra seconds spent entering data correctly will save you hours of cleanup later!
            </p>
          </div>
        </section>

        {/* Understanding Each Field */}
        <section id="fields" className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-6 font-body">
            Understanding Each Field
          </h2>
          
          {/* Name */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body border-b border-slate-600 pb-2">
              Name
            </h3>
            <p className="text-glass-secondary mb-4 font-body">
              The specific name of the polish, plate, or item — exactly as the maker named it.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="font-bold text-green-300 mb-2 font-body flex items-center gap-2">
                  <CheckCircle2 className="icon-sm" /> Good
                </p>
                <ul className="text-green-200/80 text-sm space-y-1 font-body">
                  <li>Eclipse</li>
                  <li>Unicorn Skin</li>
                  <li>Aurora</li>
                </ul>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="font-bold text-red-300 mb-2 font-body flex items-center gap-2">
                  <XCircle className="icon-sm" /> Avoid
                </p>
                <ul className="text-red-200/80 text-sm space-y-1 font-body">
                  <li>ILNP Eclipse</li>
                  <li>Unicorn Skin (holo, pink)</li>
                  <li>Aurora - PPU Aug 2023</li>
                </ul>
              </div>
            </div>
            
            <p className="text-glass-muted text-sm font-body">
              <strong>Tips:</strong> Don't include the brand (that's what the Brand field is for!), descriptions, or tags in parentheses.
            </p>
          </div>

          {/* Brand */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body border-b border-slate-600 pb-2">
              Brand
            </h3>
            <p className="text-glass-secondary mb-4 font-body">
              The company or maker who created the item.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="font-bold text-green-300 mb-2 font-body flex items-center gap-2">
                  <CheckCircle2 className="icon-sm" /> Good
                </p>
                <ul className="text-green-200/80 text-sm space-y-1 font-body">
                  <li>ILNP</li>
                  <li>Holo Taco</li>
                  <li>Polished for Days</li>
                </ul>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="font-bold text-red-300 mb-2 font-body flex items-center gap-2">
                  <XCircle className="icon-sm" /> Avoid
                </p>
                <ul className="text-red-200/80 text-sm space-y-1 font-body">
                  <li>I Love Nail Polish</li>
                  <li>holotaco</li>
                  <li>PfD, Polished 4 Days</li>
                </ul>
              </div>
            </div>
            
            <p className="text-glass-muted text-sm font-body">
              <strong>Tips:</strong> Use consistent spelling across your collection. Pick one format and stick with it!
            </p>
          </div>

          {/* Collection Name */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body border-b border-slate-600 pb-2">
              Collection Name
            </h3>
            <p className="text-glass-secondary mb-4 font-body">
              A themed group of items released together by a brand.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="font-bold text-green-300 mb-2 font-body flex items-center gap-2">
                  <CheckCircle2 className="icon-sm" /> Good
                </p>
                <ul className="text-green-200/80 text-sm space-y-1 font-body">
                  <li>Twilight Collection</li>
                  <li>Cottagecore</li>
                  <li>The Breakfast Club</li>
                </ul>
              </div>
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="font-bold text-red-300 mb-2 font-body flex items-center gap-2">
                  <XCircle className="icon-sm" /> Avoid
                </p>
                <ul className="text-red-200/80 text-sm space-y-1 font-body">
                  <li>Fall 2023</li>
                  <li>August</li>
                  <li>PPU</li>
                </ul>
              </div>
            </div>
            
            <p className="text-glass-muted text-sm font-body">
              <strong>Tips:</strong> This is the NAME of the collection, not when it was released. Event names (PPU, HHC) go in the Event field, not here. If there's no collection name, leave it blank!
            </p>
          </div>

          {/* Release Type */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body border-b border-slate-600 pb-2">
              Release Type
            </h3>
            <p className="text-glass-secondary mb-4 font-body">
              How the item was released or sold.
            </p>
            
            <div className="bg-slate-800/60 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <tbody>
                  {[
                    { type: 'Core', desc: 'Permanently available, always in stock' },
                    { type: 'Limited Edition', desc: 'One-time release, won\'t be restocked' },
                    { type: 'Seasonal', desc: 'Released for a season (holiday, summer, etc.)' },
                    { type: 'Event', desc: 'Sold through PPU, HHC, or similar events' },
                    { type: 'Exclusive', desc: 'Only available through specific retailer/group' },
                    { type: 'Box', desc: 'Part of a Subscription, or seller with "Box" in the name' },
                    { type: 'Collab', desc: 'Collaboration with another creator/brand' },
                  ].map((row, i) => (
                    <tr key={row.type} className={i > 0 ? 'border-t border-slate-600' : ''}>
                      <td className="px-4 py-3 font-bold text-glass font-body">{row.type}</td>
                      <td className="px-4 py-3 text-glass-secondary font-body">{row.desc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Event Name & Theme */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body border-b border-slate-600 pb-2">
              Event Name & Theme
            </h3>
            <p className="text-glass-secondary mb-4 font-body">
              For items released through nail polish events.
            </p>
            
            <div className="bg-slate-800/60 rounded-xl p-4 mb-4">
              <p className="text-sm text-glass-muted mb-2 font-body">Example:</p>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-glass-muted">Event Name:</span> <span className="text-glass font-medium">PPU</span></div>
                <div><span className="text-glass-muted">Event Theme:</span> <span className="text-glass font-medium">Enchanted Forest</span></div>
                <div><span className="text-glass-muted">Release Year:</span> <span className="text-glass font-medium">2023</span></div>
                <div><span className="text-glass-muted">Release Season:</span> <span className="text-glass font-medium">Fall</span></div>
              </div>
            </div>
          </div>

          {/* Finish & Effects */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body border-b border-slate-600 pb-2">
              Finish & Effects
            </h3>
            <p className="text-glass-secondary mb-4 font-body">
              The visual effects and finish type of a polish.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <p className="font-bold text-glass mb-2 font-body">Common Finishes</p>
                <div className="bg-slate-800/60 rounded-xl overflow-hidden text-sm">
                  <table className="w-full">
                    <tbody>
                      {[
                        { name: 'Crème', desc: 'Solid, opaque, no shimmer' },
                        { name: 'Shimmer', desc: 'Fine sparkle throughout' },
                        { name: 'Metallic', desc: 'Mirror-like reflective' },
                        { name: 'Jelly', desc: 'Translucent, squishy-looking' },
                        { name: 'Crelly', desc: 'Crème + jelly hybrid' },
                      ].map((row, i) => (
                        <tr key={row.name} className={i > 0 ? 'border-t border-slate-600' : ''}>
                          <td className="px-3 py-2 font-medium text-glass font-body">{row.name}</td>
                          <td className="px-3 py-2 text-glass-secondary font-body">{row.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div>
                <p className="font-bold text-glass mb-2 font-body">Common Effects</p>
                <div className="bg-slate-800/60 rounded-xl overflow-hidden text-sm">
                  <table className="w-full">
                    <tbody>
                      {[
                        { name: 'Holo', desc: 'Rainbow sparkle in light' },
                        { name: 'Duochrome', desc: 'Shifts between 2 colors' },
                        { name: 'Multichrome', desc: 'Shifts between 3+ colors' },
                        { name: 'Magnetic', desc: 'Creates patterns with magnet' },
                        { name: 'Thermal', desc: 'Changes with temperature' },
                      ].map((row, i) => (
                        <tr key={row.name} className={i > 0 ? 'border-t border-slate-600' : ''}>
                          <td className="px-3 py-2 font-medium text-glass font-body">{row.name}</td>
                          <td className="px-3 py-2 text-glass-secondary font-body">{row.desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tags Section */}
        <section id="tags" className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 font-body flex items-center gap-2">
            <Tag className="icon-md text-purple-400" />
            Tags: Your Secret Weapon
          </h2>
          
          <p className="text-glass-secondary mb-6 font-body">
            Tags are flexible labels you create to organize your collection YOUR way. They're perfect for details that don't fit in other fields.
          </p>

          {/* Specificity Guide */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body">
              What Makes a Good Tag?
            </h3>
            <p className="text-glass-secondary mb-4 font-body">
              Be specific enough to be useful, but not so specific you'll never search for it.
            </p>
            
            <div className="grid sm:grid-cols-3 gap-4">
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                <p className="font-bold text-red-300 mb-2 font-body text-center">Too Vague</p>
                <ul className="text-red-200/80 text-sm space-y-1 font-body text-center">
                  <li>animal</li>
                  <li>flower</li>
                  <li>pretty</li>
                </ul>
              </div>
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <p className="font-bold text-green-300 mb-2 font-body text-center">Just Right ✨</p>
                <ul className="text-green-200/80 text-sm space-y-1 font-body text-center">
                  <li>bird</li>
                  <li>floral, rose</li>
                  <li>pastel</li>
                </ul>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <p className="font-bold text-amber-300 mb-2 font-body text-center">Too Specific</p>
                <ul className="text-amber-200/80 text-sm space-y-1 font-body text-center">
                  <li>yellow-bellied sapsucker</li>
                  <li>pink tea rose with dewdrops</li>
                  <li>light dusty mauve pink</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Tag Ideas */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body">
              Tag Ideas by Item Type
            </h3>
            
            <div className="space-y-4">
              <div className="bg-slate-800/60 rounded-xl p-4">
                <p className="font-bold text-glass mb-2 font-body">💅 For Polish</p>
                <div className="flex flex-wrap gap-2">
                  {['linear holo', 'scattered holo', 'glass fleck', 'UV reactive', 'scented', 'cottagecore', 'witchy', 'celestial', 'one coater', 'stamping base'].map(tag => (
                    <span key={tag} className="px-2 py-1 bg-slate-700/50 rounded-full text-xs text-glass-secondary font-body">{tag}</span>
                  ))}
                </div>
              </div>
              
              <div className="bg-slate-800/60 rounded-xl p-4">
                <p className="font-bold text-glass mb-2 font-body">🔲 For Stamping Plates</p>
                <div className="flex flex-wrap gap-2">
                  {['floral', 'geometric', 'animals', 'butterfly', 'mandala', 'leaves', 'cats', 'text', 'vintage', 'line art', 'halloween', 'christmas'].map(tag => (
                    <span key={tag} className="px-2 py-1 bg-slate-700/50 rounded-full text-xs text-glass-secondary font-body">{tag}</span>
                  ))}
                </div>
              </div>
              
              <div className="bg-slate-800/60 rounded-xl p-4">
                <p className="font-bold text-glass mb-2 font-body">✨ For Embellishments</p>
                <div className="flex flex-wrap gap-2">
                  {['rhinestone', 'charm', 'dried flower', 'metal', 'resin', 'crystal', 'pearl', 'celestial', 'gothic', 'kawaii'].map(tag => (
                    <span key={tag} className="px-2 py-1 bg-slate-700/50 rounded-full text-xs text-glass-secondary font-body">{tag}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Best Practices */}
          <div className="mb-8">
            <h3 className="text-lg font-bold text-glass mb-3 font-body">
              Tag Best Practices
            </h3>
            
            <div className="space-y-3">
              {[
                { num: 1, title: 'Use lowercase', desc: 'Keeps things consistent and searchable' },
                { num: 2, title: 'Pick singular OR plural, not both', desc: 'Use "butterfly" or "butterflies" and stick with it' },
                { num: 3, title: 'Start broad, get specific when it matters', desc: 'A plate with one small bird: "bird" is fine. A plate that\'s ALL birds: tag them all!' },
                { num: 4, title: 'Think about how you\'d search', desc: 'What words would you type when looking for this item?' },
                { num: 5, title: 'Don\'t duplicate other fields', desc: 'If it\'s a holo polish, you don\'t need a "holo" tag (that\'s in Finish)' },
              ].map(tip => (
                <div key={tip.num} className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-500/30 text-purple-300 flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {tip.num}
                  </span>
                  <div>
                    <span className="font-bold text-glass font-body">{tip.title}</span>
                    <span className="text-glass-secondary font-body"> — {tip.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Example Scenario */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
            <p className="font-bold text-purple-300 mb-2 font-body">
              📝 Example: Tagging a forest animals plate
            </p>
            <p className="text-purple-200/80 text-sm mb-3 font-body">
              You have a stamping plate with forest animals including a fox, deer, owl, and some mushrooms.
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {['animals', 'forest', 'fox', 'deer', 'owl', 'mushrooms', 'nature', 'woodland'].map(tag => (
                <span key={tag} className="px-2 py-1 bg-purple-500/20 rounded-full text-xs text-purple-200 font-body">{tag}</span>
              ))}
            </div>
            <p className="text-purple-200/60 text-xs font-body">
              ✓ Broad search finds it (animals) · ✓ Theme search finds it (forest, woodland) · ✓ Specific search finds it (fox, owl) · ✓ Non-animal elements captured (mushrooms)
            </p>
          </div>
        </section>

        {/* Quick Reference */}
        <section id="quick-reference" className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 font-body flex items-center gap-2">
            <Lightbulb className="icon-md text-yellow-400" />
            Quick Reference: What Goes Where?
          </h2>
          
          <div className="bg-slate-800/60 rounded-xl overflow-hidden overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left font-bold text-glass font-body">Information</th>
                  <th className="px-4 py-3 text-left font-bold text-glass font-body">Where It Goes</th>
                  <th className="px-4 py-3 text-left font-bold text-glass font-body">Example</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { info: 'Polish name', field: 'Name', example: 'Midnight Sky' },
                  { info: 'Who made it', field: 'Brand', example: 'Polished for Days' },
                  { info: 'Product line', field: 'Line', example: 'Insta-Dri' },
                  { info: 'Collection name', field: 'Collection', example: 'Celestial Dreams' },
                  { info: 'When released', field: 'Release Year/Season', example: '2023, Fall' },
                  { info: 'How it was sold', field: 'Release Type', example: 'Event' },
                  { info: 'Which event', field: 'Event Name', example: 'PPU' },
                  { info: 'Event\'s theme', field: 'Event Theme', example: 'Mystical Creatures' },
                  { info: 'Main color', field: 'Color Family', example: 'Blue' },
                  { info: 'How it looks', field: 'Finish', example: 'Linear Holo' },
                  { info: 'Your swatch ref #', field: 'Swatch Number', example: '42' },
                  { info: 'Everything else', field: 'Tags', example: 'celestial, night sky, stars' },
                ].map((row, i) => (
                  <tr key={row.info} className={i > 0 ? 'border-t border-slate-600' : ''}>
                    <td className="px-4 py-3 text-glass-secondary font-body">{row.info}</td>
                    <td className="px-4 py-3 font-medium text-glass font-body">{row.field}</td>
                    <td className="px-4 py-3 text-purple-300 font-body">{row.example}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Common Mistakes */}
        <section id="mistakes" className="glass-card p-6 mb-6">
          <h2 className="text-xl font-bold text-glass mb-4 font-body">
            Common Mistakes to Avoid
          </h2>
          
          <div className="space-y-6">
            {/* Mistake 1 */}
            <div>
              <div className="flex items-start gap-3 mb-2">
                <XCircle className="icon-md text-red-400 flex-shrink-0 mt-0.5" />
                <p className="font-bold text-glass font-body">Putting everything in the name</p>
              </div>
              <div className="ml-8 grid sm:grid-cols-2 gap-2">
                <div className="bg-red-500/10 rounded-lg p-3">
                  <p className="text-xs text-red-300 mb-1">❌ Don't</p>
                  <p className="text-sm text-red-200/80 font-body">"ILNP Eclipse Multichrome Purple Blue Shift 2022"</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3">
                  <p className="text-xs text-green-300 mb-1">✅ Do</p>
                  <p className="text-sm text-green-200/80 font-body">Name: Eclipse | Brand: ILNP | Finish: Multichrome</p>
                </div>
              </div>
            </div>
            
            {/* Mistake 2 */}
            <div>
              <div className="flex items-start gap-3 mb-2">
                <XCircle className="icon-md text-red-400 flex-shrink-0 mt-0.5" />
                <p className="font-bold text-glass font-body">Using collection name for dates</p>
              </div>
              <div className="ml-8 grid sm:grid-cols-2 gap-2">
                <div className="bg-red-500/10 rounded-lg p-3">
                  <p className="text-xs text-red-300 mb-1">❌ Don't</p>
                  <p className="text-sm text-red-200/80 font-body">Collection: "August 2023"</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3">
                  <p className="text-xs text-green-300 mb-1">✅ Do</p>
                  <p className="text-sm text-green-200/80 font-body">Release Year: 2023 | Release Season: Summer</p>
                </div>
              </div>
            </div>
            
            {/* Mistake 3 */}
            <div>
              <div className="flex items-start gap-3 mb-2">
                <XCircle className="icon-md text-red-400 flex-shrink-0 mt-0.5" />
                <p className="font-bold text-glass font-body">Inconsistent brand names</p>
              </div>
              <div className="ml-8 grid sm:grid-cols-2 gap-2">
                <div className="bg-red-500/10 rounded-lg p-3">
                  <p className="text-xs text-red-300 mb-1">❌ Don't</p>
                  <p className="text-sm text-red-200/80 font-body">"Holo Taco" / "HT" / "holotaco"</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3">
                  <p className="text-xs text-green-300 mb-1">✅ Do</p>
                  <p className="text-sm text-green-200/80 font-body">Always use: "Holo Taco"</p>
                </div>
              </div>
            </div>
            
            {/* Mistake 4 */}
            <div>
              <div className="flex items-start gap-3 mb-2">
                <XCircle className="icon-md text-red-400 flex-shrink-0 mt-0.5" />
                <p className="font-bold text-glass font-body">Over-tagging with obvious info</p>
              </div>
              <div className="ml-8 grid sm:grid-cols-2 gap-2">
                <div className="bg-red-500/10 rounded-lg p-3">
                  <p className="text-xs text-red-300 mb-1">❌ Don't</p>
                  <p className="text-sm text-red-200/80 font-body">Tags on a red polish: red, red polish, nail polish</p>
                </div>
                <div className="bg-green-500/10 rounded-lg p-3">
                  <p className="text-xs text-green-300 mb-1">✅ Do</p>
                  <p className="text-sm text-green-200/80 font-body">Tags: vampy, fall vibes, one coater</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Still Have Questions */}
        <section className="glass-card p-6 text-center">
          <h2 className="text-xl font-bold text-glass mb-2 font-body">
            Still Have Questions?
          </h2>
          <p className="text-glass-secondary mb-4 font-body">
            Data organization is personal — there's no single "right" way! The best system is one you'll actually use consistently.
          </p>
          <p className="text-glass-muted text-sm mb-6 font-body">
            If you're importing from another app and your data is messy, don't worry! You can always clean it up over time.
          </p>
          <Link 
            to="/help" 
            className="inline-flex items-center gap-2 px-6 py-3 btn-primary rounded-xl font-medium transition-default font-body"
          >
            <ArrowLeft className="icon-sm" />
            Back to Help Center
          </Link>
        </section>
      </div>
    </div>
  );
}
